import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCrown, FaStar, FaGem, FaAward } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const Sponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const sponsorsRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sponsorsRef.current) observer.observe(sponsorsRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const cacheKey = 'sponsorsData';
    const cacheTimestampKey = 'sponsorsDataTimestamp';

    const fetchSponsors = async () => {
      try {
        const cachedData = localStorage.getItem(cacheKey);
        const cachedTimestamp = localStorage.getItem(cacheTimestampKey);

        const isCacheValid =
          cachedTimestamp &&
          new Date().getTime() - new Date(parseInt(cachedTimestamp)).getTime() < 1 * 1000;

        if (cachedData && isCacheValid) {
          setSponsors(JSON.parse(cachedData));
        } else {
          const response = await fetch('https://hub.mileageriot.com/api/sponsors');
          const data = await response.json();
          const sponsorsWithUsers = categorizeUsers(data);
          setSponsors(sponsorsWithUsers);
          localStorage.setItem(cacheKey, JSON.stringify(sponsorsWithUsers));
          localStorage.setItem(cacheTimestampKey, new Date().getTime().toString());
        }
      } catch (error) {
        console.error('Error fetching sponsors data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSponsors();
  }, []);

  const categorizeUsers = (data) => {
    const goldVip = [];
    const silverVip = [];
    const bronzeVip = [];
    const platinumVip = [];

    data.forEach((user) => {
      if (user.premium === 1) {
        goldVip.push(user);
      } else if (user.premium === 2) {
        silverVip.push(user);
      } else if (user.premium === 3) {
        bronzeVip.push(user);
      } else if (user.premium === 4) {
        platinumVip.push(user);
      }
    });

    return {
      goldVip,
      silverVip,
      bronzeVip,
      platinumVip,
    };
  };

  const getVipConfig = (type) => {
    const configs = {
      platinumVip: {
        icon: FaGem,
        color: 'text-platinum',
        gradient: 'bg-gradient-platinum',
        image: 'https://hub.mileageriot.com/assets/images/vip_4.png',
        title: t('sponsors.platinumVip')
      },
      goldVip: {
        icon: FaCrown,
        color: 'text-gold',
        gradient: 'bg-gradient-gold',
        image: 'https://hub.mileageriot.com/assets/images/vip_1.png',
        title: t('sponsors.goldVIP')
      },
      silverVip: {
        icon: FaStar,
        color: 'text-silver',
        gradient: 'bg-gradient-silver',
        image: 'https://hub.mileageriot.com/assets/images/vip_2.png',
        title: t('sponsors.silverVIP')
      },
      bronzeVip: {
        icon: FaAward,
        color: 'text-bronze',
        gradient: 'bg-gradient-bronze',
        image: 'https://hub.mileageriot.com/assets/images/vip_3.png',
        title: t('sponsors.bronzeVIP')
      }
    };
    return configs[type];
  };

  const renderVipSection = (type, users, index) => {
    if (!users || users.length === 0) return null;
    
    const config = getVipConfig(type);
    const IconComponent = config.icon;

    return (
      <div key={type} className="vip-section fade-in-up" style={{ animationDelay: `${index * 0.2}s` }}>
        <div className="vip-header">
          <div className="vip-title-container">
            <h3 className="vip-title text-white">
              <img loading="lazy" decoding="async" src={config.image} width={40} alt={`${config.title} Icon`} className="vip-badge-icon" />
              {config.title}
            </h3>
          </div>
          <div className="vip-divider"></div>
        </div>
        
        <div className="vip-grid">
          {users.map((user, userIndex) => (
            <div 
              key={user.id} 
              className="vip-member-card glass-container"
              style={{ animationDelay: `${(index * 0.2) + (userIndex * 0.1)}s` }}
            >
              <div className="vip-member-avatar-container">
                <img loading="lazy" decoding="async"
                  className="vip-member-avatar"
                  src={user.avatar}
                  alt={user.name}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(user.name) + '&color=00cfe8&background=1d1e26&size=128';
                  }}
                />
              </div>
              <h4 className="vip-member-name text-white">{user.name}</h4>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <section className="sponsors-modern page-sponsors" ref={sponsorsRef}>
        <div className="container">
          <div className="text-center">
            <div className="loading-spinner">
              <div className="spinner-modern"></div>
            </div>
            <p className="mt-3 text-white-50">{t('sponsors.loading')}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
    <Helmet>
      <title>Спонсоры — Mileage Riot ВТК | ETS2 & ATS</title>
      <meta name="description" content="Спонсоры Mileage Riot — поддержите виртуальную транспортную компанию." />
      <link rel="canonical" href="https://mileageriot.com/sponsors" />
      <meta property="og:title" content="Спонсоры — Mileage Riot" />
      <meta property="og:url" content="https://mileageriot.com/sponsors" />
      <meta property="og:locale" content="ru_RU" />
    </Helmet>
    <section className="sponsors-modern page-sponsors" ref={sponsorsRef}>
      <div className="container">
        {/* Section Header */}
        <div className="section-header text-center mb-5 fade-in-up stagger-1">
          <div className="section-badge">
            <FaCrown className="me-2" />
            <span>{t('sponsors.badge')}</span>
          </div>
          <h2 className="section-title text-glow text-white">
            {t('sponsors.title')}
          </h2>
          <div className="section-decoration mx-auto"></div>
          <p className="text-white-50 mt-3 section-subtitle">
            {t('sponsors.subtitle')}
          </p>
        </div>

        {/* VIP Sections */}
        <div className="sponsors-content">
          {renderVipSection('platinumVip', sponsors.platinumVip, 0)}
          {renderVipSection('goldVip', sponsors.goldVip, 1)}
          {renderVipSection('silverVip', sponsors.silverVip, 2)}
          {renderVipSection('bronzeVip', sponsors.bronzeVip, 3)}
        </div>
      </div>
      
      {/* Background decoration */}
      <div className="sponsors-bg-decoration"></div>
    </section>
    </>
  );
};

export default Sponsors;